import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import validator from "validator";

import CustomInput from "../../../components/CustomInput";

import { getCountryList } from "../../../services/CommonService";
import { register, resendEmailCode } from "../../../services/AuthService";
import Utils from "../../../utils";
import {
  CLIENT_LOCAL_STORE,
  COUNTRY_ID,
  PASSWORD_RULES,
  SALT,
} from "../../../configs/AppConfig";
import { encryptData } from "../../../utils/crypto";
import { Lang } from "../../../lang";
import Logo from "../../../assets/Images/Docusafe_logo.webp";
import "./Auth.css";

function Register() {
  const navigate = useNavigate();

  document.title = Lang.sign_up_title;

  let passwordRuleList = PASSWORD_RULES;
  const [passwordRules, setPasswordRules] = useState(passwordRuleList);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [country, setCountry] = useState(COUNTRY_ID);
  const [countryList, setCountryList] = useState([]);
  const [isVerify, setIsVerify] = useState(1);
  const [code, setCode] = useState("");
  const [verificationCode, setVerificationCode] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const [isShowPasswordContain, setIsShowPasswordContain] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  let errorsObj = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    company_name: "",
    confirm_password: "",
    country: "",
    code: "",
    isCheck: "",
  };
  const [errors, setErrors] = useState(errorsObj);

  useEffect(() => {
    Utils.getCurrentCountryCode().then((countryCode) => {
      getCountryList().then((response) => {
        let list = response.data.data;
        setCountryList(list);

        if (countryCode) {
          let index = list.findIndex((x) => x.icon === countryCode);
          if (index > -1) setCountry(parseInt(list[index]["id"]));
        }
      });
    });
  }, []);

  const handleCountry = (e) => setCountry(e.target.value);

  const handlePassword = (e) => {
    let value = e.target.value;
    setPassword(value);

    let updated = [...passwordRuleList];
    updated[0].active = value.length >= 8;
    updated[1].active = /[A-Z]/.test(value);
    updated[2].active = /[a-z]/.test(value);
    updated[3].active = /[0-9]/.test(value);
    updated[4].active = /[~!@#$%^&]/.test(value);

    setPasswordRules(updated);
  };

  const onRegister = (e) => {
    e.preventDefault();

    let error = false;
    const errorObj = { ...errorsObj };
    const noSpecialChar = /^[^*|":<>[\]{}`\\()';@&$]+$/;

    if (!firstName) {
      errorObj.first_name = "First name is required";
      error = true;
    } else if (!noSpecialChar.test(firstName)) {
      errorObj.first_name = "Invalid characters";
      error = true;
    }

    if (!lastName) {
      errorObj.last_name = "Last name is required";
      error = true;
    } else if (!noSpecialChar.test(lastName)) {
      errorObj.last_name = "Invalid characters";
      error = true;
    }

    if (companyName && !noSpecialChar.test(companyName)) {
      errorObj.company_name = "Invalid characters";
      error = true;
    }

    if (!email) {
      errorObj.email = "Email is required";
      error = true;
    } else if (!validator.isEmail(email)) {
      errorObj.email = "Invalid email";
      error = true;
    }

    if (!password) {
      errorObj.password = "Password is required";
      error = true;
    } else if (!validator.isStrongPassword(password)) {
      errorObj.password = "Weak password";
      error = true;
    }

    if (!confirmPassword) {
      errorObj.confirm_password = "Confirm password required";
      error = true;
    } else if (confirmPassword !== password) {
      errorObj.confirm_password = "Passwords do not match";
      error = true;
    }

    if (!country) {
      errorObj.country = "Country is required";
      error = true;
    }

    if (!isChecked) {
      errorObj.isCheck = "Please accept terms";
      error = true;
    }

    setErrors(errorObj);
    if (error) return;

    setLoading(true);

    resendEmailCode({
      email,
      first_name: firstName,
      last_name: lastName,
    })
      .then((response) => {
        setLoading(false);
        setIsVerify(2);
        setVerificationCode(response.data.code);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  const onVerify = (e) => {
    e.preventDefault();

    let errorObj = { ...errorsObj };
    let error = false;

    if (!code) {
      errorObj.code = "Code is required";
      error = true;
    } else if (parseInt(code) !== verificationCode) {
      errorObj.code = "Invalid code";
      error = true;
    }

    setErrors(errorObj);
    if (error) return;

    setLoading(true);

    register({
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      email,
      password,
      mobile: "",
      country_id: country,
      is_trial: true,
    })
      .then((response) => {
        setUserData(response.data.data);
        localStorage.setItem(
          CLIENT_LOCAL_STORE,
          encryptData(response.data.data, SALT)
        );
        Utils.updateLastActivity();
        setIsVerify(3);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  const onDashboard = () => {
    localStorage.setItem(CLIENT_LOCAL_STORE, encryptData(userData, SALT));
    navigate("/client-portal");
  };

  return (
    <div className="register-container">

      <div className="register-card fade-in">

        <h2 className="register-title">Sign Up</h2>

        {/* Stepper */}
        <div className="stepper">
          <div className={`step ${isVerify === 1 ? "active" : ""}`}>
            <span className={`step-circle ${isVerify === 1 ? "active" : ""}`}>
              1
            </span>
            Details
          </div>

          <div className="dashed"></div>

          <div className={`step ${isVerify > 1 ? "active" : ""}`}>
            <span className={`step-circle ${isVerify > 1 ? "active" : ""}`}>
              2
            </span>
            Email Confirmation
          </div>
        </div>

        {/* ================= STEP 1 ================= */}
        {isVerify === 1 && (
          <form onSubmit={onRegister}>

            <div className="row">

              <div className="col-lg-6 mb-3">
                <CustomInput
                  label="First Name"
                  name="firstName"
                  value={firstName}
                  required
                  onChange={(e) => setFirstName(e.target.value)}
                  error={errors.first_name}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <CustomInput
                  label="Last Name"
                  name="lastName"
                  value={lastName}
                  required
                  onChange={(e) => setLastName(e.target.value)}
                  error={errors.last_name}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <CustomInput
                  label="Email"
                  name="email"
                  value={email}
                  required
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <label className="form-label">Country <sup>*</sup></label>
                <select
                  className="form-select"
                  value={country}
                  onChange={handleCountry}
                >
                  <option value="">Select Country</option>
                  {countryList.map((item, i) => (
                    <option key={i} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <div className="register-error">{errors.country}</div>
                )}
              </div>

              <div className="col-lg-12 mb-3">
                <CustomInput
                  label="Company Name"
                  name="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  error={errors.company_name}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <CustomInput
                  label="Password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={handlePassword}
                  error={errors.password}
                  onFocus={() => setIsShowPasswordContain(true)}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <CustomInput
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirm_password}
                />
              </div>

              {isShowPasswordContain && (
                <div className="col-lg-12">
                  <div className="password_instruction mb-3">
                    <p className="mb-2 fw-bold text-dark">
                      Password must contain:
                    </p>
                    <ul>
                      {passwordRules.map((item, i) => (
                        <li
                          key={i}
                          className={
                            item.active ? "text-success" : "text-danger"
                          }
                        >
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="col-lg-12 mb-3">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                />
                <span className="ms-2">
                  I agree with{" "}
                  <NavLink to="/terms-condition" target="_blank">
                    Terms & Conditions
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to="/privacy-policy" target="_blank">
                    Privacy Policy
                  </NavLink>
                </span>
                {errors.isCheck && (
                  <div className="register-error">{errors.isCheck}</div>
                )}
              </div>

              <div className="col-lg-12">
                <button className="login-btn-page w-100">Next</button>
              </div>

              <div className="col-lg-12 mt-3">
                <p>
                  Already have an account?
                  <NavLink to="/login" className="login-link ms-1">
                    Login Now
                  </NavLink>
                </p>
              </div>
            </div>
          </form>
        )}

        {/* ================= STEP 2 ================= */}
        {isVerify === 2 && (
          <div className="text-center">

            <p>
              Verify your email: <strong>{email}</strong>
            </p>

            <form onSubmit={onVerify}>
              <div className="mb-3 w-75 mx-auto">
                <CustomInput
                  label="Verification Code"
                  name="verifyCode"
                  type="number"
                  value={code}
                  required
                  onChange={(e) => setCode(e.target.value)}
                  error={errors.code}
                />
              </div>

              <button className="login-btn-page w-75">Confirm Email</button>
            </form>
          </div>
        )}

        {/* ================= STEP 3 ================= */}
        {isVerify === 3 && (
          <div className="text-center">

            <label className="form-label d-block mb-4">
              You have successfully completed the sign up process.
            </label>

            <button
              className="login-btn-page w-50"
              onClick={onDashboard}
            >
              Go to Dashboard
            </button>

          </div>
        )}

      </div>
    </div>
  );
}

export default Register;
